import { useState } from "react";
import type { Movie } from "@/types/movie";
import Modal from "@/components/base/Modal";

interface PlayerDemoProps {
  movie: Movie;
}

export default function PlayerDemo({ movie }: PlayerDemoProps) {
  const [noticeOpen, setNoticeOpen] = useState(false);

  return (
    <div id="player" className="scroll-mt-24">
      <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
        <button
          type="button"
          onClick={() => setNoticeOpen(true)}
          aria-label={`播放${movie.title}（演示）`}
          className="group relative block aspect-video w-full cursor-pointer overflow-hidden"
        >
          <img
            src={movie.backdrop || movie.poster}
            alt={`${movie.title}播放画面预览`}
            title={`${movie.title} 在线观看 - 免费看大片`}
            className="absolute inset-0 h-full w-full object-cover object-center opacity-60 transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/30 to-background-950/40" />

          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-background-950/70 px-2 py-1 font-heading text-[10px] font-semibold tracking-wide text-foreground-100 backdrop-blur-sm">
            <i className="ri-hd-line text-sm text-accent-400" />
            HD 1080P
          </span>

          <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform duration-300 group-hover:scale-110 md:h-20 md:w-20">
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </span>
            <span className="text-xs text-foreground-300 md:text-sm">点击播放（演示区域）</span>
          </span>

          <span className="absolute bottom-14 left-3 inline-flex items-center gap-1.5 rounded-md bg-background-950/70 px-2.5 py-1.5 text-[11px] text-foreground-300 backdrop-blur-sm">
            <i className="ri-information-line text-sm text-accent-400" />
            真实播放功能待接入
          </span>
        </button>

        <div className="flex flex-col gap-2 border-t border-background-200/60 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNoticeOpen(true)}
              aria-label="播放（演示）"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-100 transition-colors hover:bg-background-200/70"
            >
              <i className="ri-play-fill text-lg" />
            </button>
            <button
              type="button"
              onClick={() => setNoticeOpen(true)}
              aria-label="音量（演示）"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-200/70 hover:text-foreground-100"
            >
              <i className="ri-volume-up-line text-base" />
            </button>
            <span className="font-heading text-[11px] tracking-wide text-foreground-500">
              00:00 / {String(Math.floor(movie.duration / 60)).padStart(2, "0")}:00
            </span>
          </div>

          <div className="flex flex-1 items-center gap-3 sm:mx-4">
            <span className="relative h-1 w-full overflow-hidden rounded-full bg-background-300">
              <span className="absolute inset-y-0 left-0 w-[3%] rounded-full bg-primary-500" />
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setNoticeOpen(true)}
              aria-label="清晰度（演示）"
              className="cursor-pointer rounded-md border border-background-300 px-2 py-1 font-heading text-[10px] text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300"
            >
              1080P
            </button>
            <button
              type="button"
              onClick={() => setNoticeOpen(true)}
              aria-label="全屏（演示）"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-200/70 hover:text-foreground-100"
            >
              <i className="ri-fullscreen-line text-base" />
            </button>
          </div>
        </div>
      </div>

      <Modal
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        title="播放功能说明"
      >
        <p>
          当前页面展示的是播放区域的界面与交互演示，
          <strong className="text-foreground-100">真实播放功能待接入</strong>
          ，暂时无法播放实际影片内容。
        </p>
        <p className="mt-3">
          {movie.title} 的影片资料、评分、简介与相关推荐均为完整可浏览内容，你可以先把喜欢的影片加入收藏。
        </p>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => setNoticeOpen(false)}
            className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            我知道了
          </button>
        </div>
      </Modal>
    </div>
  );
}